import React, {
  useEffect,
  useRef,
  useState,
  MouseEvent,
} from 'react';
import clsx from 'clsx';
import {
  useForm,
  FormProvider,
} from 'react-hook-form';
import RadioGroup from '@mui/material/RadioGroup';
import isNil from 'lodash/isNil';
import Controller from 'core/components/FormController';
import {
  NPISearchItemDTO,
} from 'dtos';
import {
  notEmptyString,
} from '@populateui/utils';
import MaskedTextField from 'core/components/MaskedTextField';
import SearchIcon from 'core/icons/search';
import Button from 'pages/Dashboard/components/Button';
import {
  useSelectableContext,
} from 'core/hooks';
import {
  useProvidersSearch,
} from 'pages/Dashboard/hooks/providers';
import Input from 'pages/Dashboard/components/Input';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import USStateSelect from 'pages/Dashboard/components/USStateSelect';
import Spinner from 'pages/Dashboard/components/Spinner';
import SearchResult from 'pages/Dashboard/components/ReferringProvider/SearchResult';
import DisabledFormTooltip from 'core/components/DisabledFormTooltip';
import ToolTip from 'core/components/Tooltip';
import Radio from 'pages/Dashboard/components/Input/Radio';
import {
  validateZipCode,
} from 'utils/react-hook-form';
import ZipCodeTextField from 'core/components/ZipCodeTextField';
import {
  ProviderSearchParams,
} from 'pages/Dashboard/types/provider';

type Props = {
  onClose: () => void;
  onChange: (selectedProvider?: NPISearchItemDTO | null) => void;
  hasSuggestionSelection?: boolean;
  selectionResetKey?: number;
  onSearchSelectionChange?: (provider: NPISearchItemDTO | null) => void;
  onConfirmSuggestion?: () => void;
  value?: ProviderSearchParams | null;
  isManual: boolean;
};

const normalizeQueryParams = ({ npi, npiType, ...params }: ProviderSearchParams) => (
  notEmptyString(npi) && npi?.length === 10
    ? { npi, npiType } : { ...params, npiType }
);

function validateState({
  firstName,
  lastName,
  organizationName,
  state,
}: ProviderSearchParams) {
  return notEmptyString(state) && [firstName, lastName, organizationName].some(notEmptyString);
}

function validate(
  searchParams?: ProviderSearchParams,
  isStateValidation?: boolean,
) {
  const isNPIValid = (searchParams?.npi ?? '').length === 10;
  const isStateValid = validateState(searchParams ?? {});

  const message = isStateValidation
    ? 'State is required with either First Name and/or Last Name or Organization Name provided.'
    : 'NPI should contain at least 10 characters';

  return isNPIValid || isStateValid ? true : message;
}

function validateNPIWithState(
  value?: unknown,
  searchParams?: ProviderSearchParams,
) {
  return validate(searchParams, false);
}

function validateStateWithNPI(
  value?: string | null,
  searchParams?: ProviderSearchParams,
) {
  return validate(searchParams, true);
}

export default function ProviderSearchContent({
  onClose,
  onChange,
  hasSuggestionSelection = false,
  selectionResetKey = 0,
  onSearchSelectionChange,
  onConfirmSuggestion,
  value,
  isManual,
}: Props) {
  const defaultValues = isNil(value) ? null : value;
  const prevNPIType = useRef<string>(value?.npiType ?? 'NPI-1 Individual');
  const defaultQueryParams = isNil(defaultValues)
    ? null : normalizeQueryParams(defaultValues);

  const formProps = useForm<ProviderSearchParams>({
    mode: 'all',
    defaultValues: defaultValues ?? { npiType: 'NPI-1 Individual' },
  });

  const {
    control,
    getValues,
    handleSubmit,
    formState: { isValid },
    trigger,
    watch,
    setValue,
  } = formProps;

  const [selectedRow, setSelectedRow] = useState<NPISearchItemDTO | null>(null);
  const [
    queryParams,
    setQueryParams,
  ] = useState<ProviderSearchParams | null>(defaultQueryParams);
  const { clear } = useSelectableContext();

  const {
    data,
    isFetching: isSearching,
    isFetched,
  } = useProvidersSearch(queryParams);

  const npiType = watch('npiType');
  const isOrganization = npiType === 'NPI-2 Organization';

  const onSearch = ({
    npi,
    npiType,
    state,
    firstName,
    lastName,
    organizationName,
    zipCode,
  }: ProviderSearchParams) => {
    const params = {
      npi,
      npiType,
      state,
      zipCode,
      ...(isOrganization ? { organizationName } : { firstName, lastName }),
    };
    setQueryParams(normalizeQueryParams(params));
  };

  const onProviderSelect = (provider?: NPISearchItemDTO | null) => {
    onChange?.(provider);
  };

  const onSearchSelection = (provider: NPISearchItemDTO | null) => {
    setSelectedRow(provider);
    onSearchSelectionChange?.(provider);
  };

  const onConfirm = (provider?: NPISearchItemDTO | null) => {
    onProviderSelect(isManual ? {
      ...provider,
      taxonomyCode: '000000000X', // TODO: Remove this once we have a proper taxonomy code
    } : selectedRow);
    onClose();
  };

  const handleConfirm = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (hasSuggestionSelection) {
      onConfirmSuggestion?.();
    } else {
      handleSubmit(onConfirm)();
    }
  };

  const triggerSearch = async () => {
    const isValid = await trigger();
    if (isValid) { onSearch(getValues()); }
  };

  useEffect(() => {
    if (!isManual) { triggerSearch(); }
  }, [isManual]);

  useEffect(() => {
    if (prevNPIType.current !== npiType) {
      (async () => {
        prevNPIType.current = npiType!;
        setValue('firstName', null, { shouldValidate: true });
        setValue('lastName', null, { shouldValidate: true });
        setValue('organizationName', null, { shouldValidate: true });
        setSelectedRow(null);
        onSearchSelectionChange?.(null);
        setQueryParams(null);
        await trigger();
      })();
    }
  }, [npiType]);

  useEffect(() => {
    if (selectionResetKey > 0) {
      setSelectedRow(null);
      clear();
    }
  }, [clear, selectionResetKey]);

  const handleClose = () => {
    setSelectedRow(null);
    onClose();
  };

  const ConfirmButtonWrapper = isManual ? DisabledFormTooltip : ToolTip;
  const disabled = isManual
    ? !isValid
    : !hasSuggestionSelection && isNil(selectedRow);

  return (
    <FormProvider {...formProps}>
      <div className={clsx('px-4 flex flex-col justify-between gap-form-row populate-form', {
        'flex-1': isManual,
      })}
      >
        <div className="flex flex-col gap-form-row">
          <Controller
            name="npiType"
            control={control}
            render={({ field }) => (
              <Wrapper
                id="npi-type"
                className="text-xs w-full"
                label="NPI Type"
              >
                <RadioGroup
                  row
                  className="mt-2 px-2 w-72 flex justify-between"
                  {...field}
                >
                  {['NPI-1 Individual', 'NPI-2 Organization'].map((npiType) => (
                    <Radio
                      key={npiType}
                      value={npiType}
                      label={npiType}
                    />
                  ))}
                </RadioGroup>
              </Wrapper>
            )}
          />
          <div className="flex items-center justify-between gap-form-field w-full">
            {isOrganization
              ? (
                <Controller
                  name="organizationName"
                  control={control}
                  rules={{ required: isManual ? 'Organization Name is Required' : false }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      required={isManual}
                      className="w-full"
                      id="organization-name"
                      placeholder="Organization Name"
                      label="Organization Name"
                    />
                  )}
                />
              )
              : (
                <>
                  <Controller
                    name="firstName"
                    control={control}
                    rules={
                      isManual
                        ? { required: 'First Name is Required' }
                        : { required: false }
                    }
                    render={({ field }) => (
                      <Input
                        {...field}
                        required={isManual}
                        className="w-full"
                        id="first-name"
                        placeholder="First Name"
                        label="First Name"
                      />
                    )}
                  />
                  <Controller
                    name="lastName"
                    control={control}
                    rules={
                      isManual
                        ? { required: 'Last Name is Required' }
                        : { required: false }
                    }
                    render={({ field }) => (
                      <Input
                        {...field}
                        required={isManual}
                        className="w-full"
                        id="last-name"
                        placeholder="Last Name"
                        label="Last Name"
                      />
                    )}
                  />
                </>
              )}
            <Controller
              name="state"
              control={control}
              rules={
                isManual
                  ? { required: 'State is Required' }
                  : {
                    required: false,
                    validate: validateStateWithNPI,
                  }
              }
              render={({ field }) => (
                <Wrapper
                  id="state"
                  required={isManual}
                  className="w-full"
                  label="State"
                >
                  <USStateSelect
                    {...field}
                    className="w-full"
                  />
                </Wrapper>
              )}
            />
            {!isManual && (
              <Controller
                control={control}
                name="zipCode"
                rules={{
                  validate: (value) => {
                    const result = validateZipCode(value);
                    return result ? undefined : 'Zipcode has to be 5 or 9 digits.';
                  },
                }}
                render={({ field, fieldState: { invalid } }) => (
                  <Wrapper
                    id="npi-zip-code"
                    className="w-full"
                    label="Zipcode"
                    error={invalid}
                    showErrorText={false}
                  >
                    <ZipCodeTextField
                      {...field}
                      className="w-full"
                    />
                  </Wrapper>
                )}
              />
            )}
            <Controller
              name="npi"
              control={control}
              rules={isManual ? {
                required: 'NPI is Required',
                minLength: 10,
                maxLength: 10,
              } : {
                required: false,
                validate: validateNPIWithState,
              }}
              render={({ field }) => (
                <Wrapper
                  className="w-full"
                  id="npi-number"
                  label="NPI"
                  required={isManual}
                >
                  <MaskedTextField
                    onBlur={field.onBlur}
                    value={typeof field.value === 'string' ? field.value : ''}
                    onAccept={field.onChange}
                    unmask
                    mask={[{
                      mask: '0000000000',
                      lazy: false,
                    }]}
                  />
                </Wrapper>
              )}
            />
            {!isManual && (
              <DisabledFormTooltip>
                <Button
                  variant="outlined"
                  type="button"
                  color="primary"
                  className="nw-search-provider !border-blue-500 disabled:!border-gray-300 !h-8 top-2"
                  onClick={handleSubmit(onSearch)}
                  disabled={isSearching || !isValid}
                >
                  {isSearching ? <Spinner /> : <SearchIcon className="!w-4 !h-4" />}
                </Button>
              </DisabledFormTooltip>
            )}
          </div>
        </div>
      </div>
      {!isManual && (
        <SearchResult
            data={data ?? []}
            isSearching={isSearching}
            queryParams={queryParams}
            setSelectedRow={onSearchSelection}
            isFetched={isFetched}
          />
      )}
      <div className="px-4 pb-4 flex justify-between">
          <Button
            variant="outlined"
            type="button"
            className="nw-create-patient"
            onClick={handleClose}
        >
          Close
        </Button>
        <ConfirmButtonWrapper
          key={isManual.toString()}
          title="Search and Select one of the providers"
          hide={!disabled}
        >
          <div className={clsx({ 'cursor-not-allowed': disabled })}>
            <Button
              variant="contained"
              type="button"
              color="primary"
              className="!border-blue-500"
              disabled={disabled}
              onClick={handleConfirm}
            >
              Confirm
            </Button>
          </div>
        </ConfirmButtonWrapper>
      </div>
    </FormProvider>
  );
}
